/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { HttpClient, HttpResponse, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CoreModule } from './@core/core.module';
import { ThemeModule } from './@theme/theme.module';
import { AppComponent } from './app.component';
import { config, routes } from './app-routing.module';

import {
  NbChatModule,
  NbDatepickerModule,
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbWindowModule,
  NbCardModule,
} from '@nebular/theme';
import {
  NbAuthModule,
  NbPasswordAuthStrategy,
  NbAuthJWTToken,
  NbPasswordAuthStrategyOptions,
  getDeepFromObject,
} from '@nebular/auth';
import { AuthGuard } from './@core/guards/auth-guard.service';
import { environment } from '../environments/environment';
import { PermissionGuard } from './@core/guards/permission-guard.service';
import { HeaderTitleService } from './@core/services/header-title.service';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AccessGuard } from './@core/guards/access-guard.service';
import { TenantProvider } from './@core/providers/tenant.provider';
import { appInitializer } from './@core/providers/app-init.providerfactory';
import { TenantService } from './@core/services/tenant.service';
import { WINDOW_PROVIDERS } from './@core/providers/window.provider';
import { StoreModule } from '@ngrx/store';
import * as tenantSettingReducer from './@core/ng-store/tenant-setting/tenant-setting.reducer';
import { TenantSettingModel } from './shared/models/tenant-setting.model';
import { NavigationGuard } from './@core/guards/navigation-guard.service';
import { AppEventService } from './@core/services/app-event.service';
import { DateService } from './@core/services/date.service';
import { DATE_SERVICE_INTERFACE } from './@core/services/interfaces/i-date-service.inteface';
import { InvoiceService } from './@core/services/invoice.service';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

export interface AppState {
  tenantSetting: TenantSettingModel
}
@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    exports: [RouterModule], imports: [BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules }),
        NbCardModule,
        ThemeModule.forRoot(),
        NbSidebarModule.forRoot(),
        NbMenuModule.forRoot(),
        NbDatepickerModule.forRoot(),
        NbDialogModule.forRoot(),
        NbWindowModule.forRoot(),
        NbToastrModule.forRoot(),
        CoreModule.forRoot(),
        NbAuthModule.forRoot({
            strategies: [
                NbPasswordAuthStrategy.setup({
                    name: 'email',
                    baseEndpoint: environment.apiUrl,
                    token: {
                        class: NbAuthJWTToken,
                        key: 'token',
                        getter: (module: string, res: HttpResponse<Object>, options: NbPasswordAuthStrategyOptions) => {
                            return getDeepFromObject(res.body, options.token.key);
                        },
                    },
                    login: {
                        endpoint: '/api/authentication/login#false',
                        method: 'post',
                        redirect: {
                            success: '/',
                            failure: null, // stay on the same page
                        },
                        defaultErrors: ['The email or password is incorrect.'],
                    },
                    logout: {
                        endpoint: '/api/authentication/logout',
                        method: 'post',
                        redirect: {
                            success: '/auth/login',
                            failure: null,
                        },
                    },
                    requestPass: {
                        endpoint: '/api/authentication/request-reset-password',
                        method: 'post',
                        redirect: {
                            success: '/auth/login',
                            failure: null,
                        },
                        defaultErrors: [
                            'The email is incorrect or you do not have an account on the system.',
                        ],
                        defaultMessages: [
                            'A password recovery link has been sent to your email address.',
                        ],
                    },
                    resetPass: {
                        endpoint: '/api/authentication/reset-password',
                        method: 'post',
                        resetPasswordTokenKey: 'token',
                        redirect: {
                            success: '/auth/login',
                            failure: null,
                        },
                        defaultMessages: ['Your password was successfully updated'],
                    },
                    validation: {
                        password: {
                            minLength: 8,
                            required: true,
                            regexp: '^(?=.*[a-z])(?=.*[A-Z])(?=.*d)(?=.*[#$^+=!*()@%&]).{8,}$',
                        },
                    },
                }),
            ],
            forms: {
                requestPassword: {
                    redirectDelay: 4000,
                },
                resetPassword: {
                    redirectDelay: 4000,
                },
                validation: {
                    password: {
                        minLength: 8,
                        required: true,
                        regexp: '^(?=.*[a-z])(?=.*[A-Z])(?=.*d)(?=.*[#$^+=!*()@%&]).{8,}$',
                    },
                },
            },
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
        }),
        StoreModule.forRoot({
            tenantSetting: tenantSettingReducer.reducer
        }),
        FontAwesomeModule], providers: [
        HeaderTitleService,
        AuthGuard,
        PermissionGuard,
        AccessGuard,
        NavigationGuard,
        TenantProvider,
        TenantService,
        InvoiceService,
        AppEventService,
        WINDOW_PROVIDERS,
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializer,
            deps: [TenantProvider, HttpClient],
            multi: true,
        },
        { provide: DATE_SERVICE_INTERFACE, useClass: DateService },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
